import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useKana } from 'react-use-kana';
import { removeNonNumericCharacters } from '../utils/util';
import { hankanaToZenkana } from '../utils/convert';
import { SEX, OLD_TEST_TYPE, HOW_DID_YOU_KNOW, HOW_DID_YOU_KNOW_LABEL } from '../entry-points/entry';
import { toHalfWidth, hiraToKana, toHalfWidthNumber } from '../utils/entry-utils';
import { useValidateState, isValidBirthday, bindStateToText, valid } from '../utils/entry-utils';
import { Text, RadioContainer, Radio, Checkbox } from './entry-form-parts';
import styled from 'styled-components'

const ErrorContainer = styled.div`
	margin-top: 1rem;
	margin-inline: -0.5rem;
	display: flex;
	flex-direction: column;
	padding: 1rem 0.5rem;
	border-radius: 0.5rem;
	border: 1px solid var(--req-text-color);
`

const ErrorHead = styled.div`
	font-weight: 700;
	font-size: 1rem;
	margin-bottom: 0.5rem;
`

const ErrorItem = styled.div`
	line-height: 2;
	position: relative;
	padding-left: 1rem;
	font-size: 0.835rem;
	&::before {
		content: '・';
		position: absolute;
		left: 0;
		top: 0;
	}
`

export function InputPersonalInformation({
	onNext, onPrev, zipcodeState, address1State, address2State, phoneNumberState, mailAddressState, confirmMailAddressState, firstNameState, firstNameKanaState, lastNameState, lastNameKanaState, sexState, birthYearState, birthMonthState, birthDateState, birthdayState, introducerIdState, addresses, howDidYouKnowState, howDidYouKnowAdMediaState, testTypeState, groupCodeState, serviceCodeState, hasEntryBeforeState
}) {
	const [address1, setAddress1] = address1State;
	const [blur, setBlur] = useState(false);
	const [birthday] = birthdayState;
	const [testType] = testTypeState;
	const { kana: firstNameKana, setKanaSource: setFirstNameKanaSource } = useKana({ kanaType: 'katakana' });
	const { kana: lastNameKana, setKanaSource: setLastNameKanaSource } = useKana({ kanaType: 'katakana' });
	const privacyAgreementState = useValidateState(false, v => v);
	const [showErrors, setShowErrors] = useState(false)
	const [errors, setErrors] = useState<{target: string, message: string}[]>([])

	useEffect(() => {
		const targets = [
			{
				isValid: valid(zipcodeState),
				target: '[name="zipcode"]',
				message: '「郵便番号」を確認してください',
			},
			{
				isValid: valid(address1State),
				target: 'select[value="' + address1 + '"]',
				message: '「住所1」を確認してください',
			},
			{
				isValid: valid(address2State),
				target: '[name="address2"]',
				message: '「住所2」を確認してください',
			},
			{
				isValid: valid(phoneNumberState),
				target: '[name="phone-number"]',
				message: '「電話番号」を確認してください',
			},
			{
				isValid: valid(mailAddressState),
				target: '[name="email"]',
				message: '「メールアドレス」を確認してください',
			},
			{
				isValid: valid(confirmMailAddressState),
				target: '[name="email-confirm"]',
				message: '「メールアドレス（確認）」を確認してください',
			},
			{
				isValid: valid(lastNameState),
				target: '[name="first-name"]',
				message: '「氏（漢字）」確認してください',
			},
			{
				isValid: valid(firstNameState),
				target: '[name="first-name-kana"]',
				message: '「名（漢字）」を確認してください',
			},
			{
				isValid: valid(lastNameKanaState),
				target: '[name="last-name"]',
				message: '「氏（フリガナ）」を確認してください',
			},
			{
				isValid: valid(firstNameKanaState),
				target: '[name="last-name-kana"]',
				message: '「名（フリガナ）」を確認してください',
			},
			{
				isValid: valid(sexState),
				target: '[name="sex"]',
				message: '「性別」を確認してください',
			},
			{
				isValid: valid(birthdayState),
				target: '[name="birth-year"], [name="birth-month"], [name="birth-date"]',
				message: '「生年月日」を確認してください',
			},
			{
				isValid: valid(hasEntryBeforeState),
				target: '[name="hasEntryBefore"]',
				message: '「以前にMetallo-balance検査を受けたことがありますか？」を確認してください',
			},
			{
				isValid: valid(introducerIdState),
				target: '[name="introducer"]',
				message: '「法人ID（数字7桁）」を確認してください',
			},
			{
				isValid: valid(privacyAgreementState),
				target: '[name="privacy-agreement"]',
				message: '個人情報の取り扱いについての内容を確認し、チェックを入れてくださいてください',
			},
			{
				isValid: valid(howDidYouKnowState),
				target: '[name="how-did-you-know"]',
				message: '「メタロ・バランスについてのアンケート」を確認してください',
			},
			{
				isValid: valid(howDidYouKnowAdMediaState),
				target: '[name="how-did-you-know-media"]',
				message: '「雑誌・新聞・広告などの名称」を確認してください',
			},
			{
				isValid: valid(groupCodeState),
				target: '[name="group-code"]',
				message: '「グループコードをお持ちの方はご入力ください」を確認してください',
			},
			{
				isValid: valid(serviceCodeState),
				target: '[name="service-code"]',
				message: '「サービスコードをお持ちの方はご入力ください」を確認してください',
			},
		]
		setErrors(targets.filter(({isValid}) => !isValid).map(({message, target}) => ({message, target})))
	}, [
		zipcodeState[0],
		address1State[0],
		address2State[0],
		phoneNumberState[0],
		mailAddressState[0],
		confirmMailAddressState[0],
		firstNameState[0],
		firstNameKanaState[0],
		lastNameState[0],
		lastNameKanaState[0],
		sexState[0],
		birthdayState[0],
		introducerIdState[0],
		howDidYouKnowState[0],
		howDidYouKnowAdMediaState[0],
		privacyAgreementState[0],
		hasEntryBeforeState[0],
		groupCodeState[0],
		serviceCodeState[0],
	])

	useEffect(() => {
		setFirstNameKanaSource(firstNameState[0]);
	}, [firstNameState[0]]);

	useEffect(() => {
		firstNameKanaState[1](firstNameKana);
	}, [firstNameKana]);

	useEffect(() => {
		setLastNameKanaSource(lastNameState[0]);
	}, [lastNameState[0]]);

	useEffect(() => {
		lastNameKanaState[1](lastNameKana);
	}, [lastNameKana]);
	useEffect(() => {
		howDidYouKnowAdMediaState[1]('');
		howDidYouKnowState[1](null);
	}, [hasEntryBeforeState[0]]);
	return <>
		<h3>お客様情報の入力</h3>
		<p className="input-notes" style={{ marginBottom: '1rem' }}>検査のお申し込み・予約は、弊社株式会社レナテックが各病院・クリニックの予約手続きを代行させて いただいております。ご入力いただく個人情報等は事務代行機関である弊社レナテックと検査会場の病院・クリニックとの間で適切に管理をさせていただきますので、ご了承ください。</p>
		<p className="input-notes" style={{ marginBottom: '1rem' }}>お申込み書類や検査結果など大切な書類をお届けするため、ご本人様のご住所でお間違いないようにご注意下さい。</p>
		<p className="input-notes">※入力に不備がある場合は入力欄の枠が赤色で表示されます。</p>
		<p className="input-notes redbold">※赤太字は必須項目です。</p>
		<Text state={zipcodeState} label="郵便番号（ハイフンなし）" replacer={removeNonNumericCharacters} placeholder="1234567" name="zipcode" />
		<div className="input-container">
			<p className="label">住所1</p>
			<div className="select-container">
				<p className={classNames('select-place-holder', 'address-place-holder', { disabled: addresses.length < 2, empty: addresses.length < 1 })}>{address1 ? address1 : '郵便番号を入力してください'}</p>
				<select disabled={addresses.length < 2} value={address1} onChange={e => setAddress1(e.target.value)}>
					{addresses.length > 0 ? addresses.map((a, i) => <option key={i} value={a}>{a}</option>) : <option value=""></option>}
				</select>
			</div>
		</div>
		<Text<string> state={address2State} label="住所2（住所の続きをご入力ください）" name="address2" replacer={str => toHalfWidth(str).replace(/,/g, '')} />
		<Text<string> state={phoneNumberState} label="電話番号" name="phone-number" replacer={str => toHalfWidthNumber(str)} />
		<Text state={mailAddressState} label="メールアドレス" name="email" />
		<p className="input-notes">*申し込み完了後に受付完了の確認メールを自動送信します。<br />
			お使いのメールサービス（Gmail等）やキャリアメール（@docomo.ne.jp、@softbank.ne.jp、@ezweb.ne.jp等）の設定により受信出来ない場合がございます。<br />
			各種設定や受信できない理由については<a href="/emailtrouble" target="_blank">自動返信メールが届かない方へ</a>をご覧ください。</p>
		<Text state={confirmMailAddressState} label="メールアドレス（確認）" name="email-confirm" />
		<div className="horizontal-container">
			<Text state={lastNameState} label="氏（漢字）" name="last-name" replacer={(v: any) => v.replace(/\s/g, '')} />
			<Text state={firstNameState} label="名（漢字）" name="first-name" replacer={(v: any) => v.replace(/\s/g, '')} />
		</div>
		<div className="horizontal-container">
			<Text state={lastNameKanaState} label="氏（フリガナ）" name="last-name-kana" replacer={(v: any) => hiraToKana(hankanaToZenkana(v.replace(/\s/g, '')))} />
			<Text state={firstNameKanaState} label="名（フリガナ）" name="first-name-kana" replacer={(v: any) => hiraToKana(hankanaToZenkana(v.replace(/\s/g, '')))} />
		</div>
		<p className="input-notes">*氏名と生年月日を元に過去に受けたMB検査結果と突合しています。2回目以降の方は前回と同じ字体で氏名を登録してください。</p>
		<RadioContainer label="性別">
			<Radio state={sexState} value={SEX.MALE} name="sex">男性</Radio>
			<Radio state={sexState} value={SEX.FEMALE} name="sex">女性</Radio>
		</RadioContainer>
		<div className="input-container">
			<p className="label">生年月日（西暦）</p>
			<div className={classNames('date-input-container', { invalid: blur && !isValidBirthday(birthday) })}>
				<label><input type="text" name="birth-year" {...bindStateToText(birthYearState)} onBlur={() => {
					const [value, setValue] = birthYearState;
					setBlur(true);
					setValue(removeNonNumericCharacters(value));
				}} />年</label>
				<label><input type="text" name="birth-month" {...bindStateToText(birthMonthState)} onBlur={() => {
					const [value, setValue] = birthMonthState;
					setBlur(true);
					setValue(removeNonNumericCharacters(value));
				}} />月</label>
				<label><input type="text" name="birth-date" {...bindStateToText(birthDateState)} onBlur={() => {
					const [value, setValue] = birthDateState;
					setBlur(true);
					setValue(removeNonNumericCharacters(value));
				}} />日</label>
			</div>
		</div>

		<div style={{ marginBottom: '2rem' }}></div>
		<RadioContainer label="以前にMetallo-balance検査を受けたことがありますか？">
			<Radio name='hasEntryBefore' value={true} state={hasEntryBeforeState}>
				はい
			</Radio>
			<Radio name='hasEntryBefore' value={false} state={hasEntryBeforeState}>
				いいえ
			</Radio>
		</RadioContainer>

		<div style={{ marginBottom: '2rem' }}></div>
		<div className="option">
		<Text state={groupCodeState} name="group-code" label="グループコードをお持ちの方はご入力ください［任意］" />

		<Text state={serviceCodeState} name="service-code" label="サービスコードをお持ちの方はご入力ください［任意］" />
		{!serviceCodeState[2]() &&
			<p className="input-notes">有効なサービスコードを入力してください</p>}

		{testType !== OLD_TEST_TYPE.CHUEI_BANK && <>
			<Text state={introducerIdState} label="法人ID（数字7桁）［任意］" name="introducer" />
			<p className="input-notes">*ご不明な場合は空欄のままでお願いします。</p>
		</>}
		</div>

		<div style={{ marginBottom: '2rem' }}></div>
		<div className="text-label">
			<div className="label">個人情報の取り扱いについて</div>
			<div className="privacy-box">
				個人情報保護方針<br />
				<br />
				株式会社 レナテック（以下「当社」）は、個人情報保護に関する法令およびその他の規範を遵守します。<br />
				<br />
				当社が収集する個人情報とは、個人の氏名、生年月日、住所など特定の個人を識別できる情報を指します。<br />
				個人情報保護について<br />
				<br />
				https://metallo-balance.net/に属するウェブサイト（以下、当ウェブサイト）を利用する事で、貴方は以下に記載する個人情報保護についての諸条件に同意した事になります。これらの諸条件に同意できない場合は、当ウェブサイトをご利用になる事ができません。<br />
				個人情報の収集<br />
				<br />
				貴方は当ウェブサイトを閲覧するにあたり、ご自身に関するいかなる情報も開示する必要はありません。 貴方は匿名のままで、当ウェブサイトを自由に閲覧する事ができます。 しかし、場合により貴方の氏名やメールアドレスなどの個人情報の開示をお願いする事があります（例：当社へのお問い合わせの際など）。 尚、貴方から当社へ個人情報を送信された場合、当社はあなたの個人情報を貴方の許可なく、第三者へ開示・共有する事はありません。<br />
				Cookieの使用<br />
				<br />
				Cookieとは、ウェブサーバーから貴方のブラウザへ送信・保持される情報です。 Cookieはその情報を送ったウェブサーバーによってのみ読まれる可能性があります。 Cookieはプログラムではなく、単独で実行されることはなく、コンピュータウィルスを内包するものでもありません。 当社ウェブサイトのアクセス性・利便性を向上させる目的で、Cookieを使い、当社ウェブサイトの閲覧状況などの情報を収集する場合があります。このデータは貴方個人を特定・追跡するものではありません。 多くのブラウザは、はじめからCookieが利用されるように設定されていますが、貴方はCookieの受け入れに際して警告を表示したり、拒否するようにブラウザの設定を変更することが可能です。<br />
				ウェブサーバーの記録<br />
				<br />
				当社のウェブサーバーは、貴方のコンピュータのIPアドレスを自動的に収集・記録しますが、これらは貴方個人を特定するものではありません。貴方自ら個人情報を開示しない限り、貴方は匿名のまま当社ウェブサイトを閲覧することが可能です。<br />
				個人情報の取得・利用について<br />
				<br />
				当社は適法かつ公正な手段によって個人情報を取得します。また、個人情報をあらかじめ通知または公表した利用目的の範囲内で利用することとし、法令に定める場合を除きあらかじめ本人の同意を得ることなく個人データを第三者に提供しません。<br />
				個人データの管理について<br />
				<br />
				当社は利用目的の達成に必要な範囲内において個人データの正確性を保ちこれを安全に管理し、個人データの紛失・改ざん・漏えい等を防止するため、不正アクセス等に対する合理的な情報セキュリティ対策を講じます。<br />
				免責事項<br />
				<br />
				当社ウェブサイトからリンクされた、当社ウェブサイト以外のウェブサイトの内容やサービスに関して、当社ウェブサイトの個人情報保護についての諸条件は適用されません。 当社ウェブサイト以外のウェブサイトの内容及び、個人情報の保護に関して、当社は責任を負いません。<br />
				個人情報に関する苦情・相談窓口<br />
				<br />
				〒259-1114 伊勢原市高森4-19-15<br />
				株式会社レナテック 個人情報保護管理責任者<br />
				メールアドレス: mb@metallo-balance.net<br />
				TEL: 0463-92-6114（受付時間：平日9:00～17:30）<br />
				FAX: 0463-74-6239<br />
			</div>
		</div>
		<Checkbox state={privacyAgreementState} name="privacy-agreement">同意する ※チェックがないと次へ進めずお申込はできません。</Checkbox>
		{!(testType === OLD_TEST_TYPE.CHUEI_BANK || testType === OLD_TEST_TYPE.GOLDEN_LOOP) && <>
			<h3>メタロ・バランスについてのアンケート</h3>
			{hasEntryBeforeState[0] ?
				<RadioContainer label="また検査を受けようと思ったきっかけはなんですか">
					{[
						HOW_DID_YOU_KNOW.MAIL_FROM_RENATECH,
						HOW_DID_YOU_KNOW.EMAIL_FROM_RENATECH,
						HOW_DID_YOU_KNOW.OTHER
					].map(value => <Radio key={value} state={howDidYouKnowState} value={value} name="how-did-you-know">{HOW_DID_YOU_KNOW_LABEL[value]}</Radio>)}
				</RadioContainer> :
				<RadioContainer label="どこでメタロ・バランスを知りましたか">
					{[
						HOW_DID_YOU_KNOW.INTRODUCE,
						HOW_DID_YOU_KNOW.PAPER_AD,
						HOW_DID_YOU_KNOW.WEB_SITE,
						HOW_DID_YOU_KNOW.RADIO_OR_TV_OR_YOUTUBE,
						HOW_DID_YOU_KNOW.WEB_SEARCH,
						HOW_DID_YOU_KNOW.HOSPITAL_OR_CLINIC,
						HOW_DID_YOU_KNOW.OTHER
					].map(value => <Radio key={value} state={howDidYouKnowState} value={value} name="how-did-you-know">{HOW_DID_YOU_KNOW_LABEL[value]}</Radio>)}
				</RadioContainer>}
			{hasEntryBeforeState[0] ?
				[HOW_DID_YOU_KNOW.OTHER].includes(howDidYouKnowState[0]) && <>
					<div className="option"><Text state={howDidYouKnowAdMediaState} label="その他の場合ご記入ください［任意］" name="how-did-you-know-media" /></div>
					<p className="input-notes">*60文字以下</p>
				</> :
				[HOW_DID_YOU_KNOW.OTHER, HOW_DID_YOU_KNOW.PAPER_AD, HOW_DID_YOU_KNOW.WEB_SITE].includes(howDidYouKnowState[0]) && <>
					<Text state={howDidYouKnowAdMediaState} label="雑誌・新聞・広告などの名称（不明な場合は「不明」とご入力下さい）" name="how-did-you-know-media" />
					<p className="input-notes">*60文字以下</p>
				</>}
		</>}
		{
			showErrors && errors.length > 0 && <ErrorContainer>
				<ErrorHead>
					次の項目に不備があります
				</ErrorHead>
				{
					errors.map((error, i) => {
						return <ErrorItem key={i}>
							{error.message}
						</ErrorItem>
					})
				}
			</ErrorContainer>
		}
		<div className="button-container" style={{ marginBottom: '2rem' }}>
			<button className="prev-button" onClick={() => onPrev()}>戻る</button>
			<div className="spacer"></div>
			<button className="next-button" onClick={() => {
				setShowErrors(true)
				if(errors.length < 1) onNext()
			}}>次へ</button>
		</div>
			<div style={{ marginBottom: '2rem' }}></div>
			<p className="input-dw">「次へ」のボタンがグレーのままで押せない場合は、再度下記の点をご確認ください。</p>
			<p className="input-dw">・郵便番号に「-（ハイフン）」を入れていませんか？ 入力例：2591114</p>
			<p className="input-dw">・電話番号に「-（ハイフン）」を入れましたか？ 入力例：090-0000-0000</p>
			<p className="input-dw">・メールアドレスとメールアドレス確認用は同じになっていますか？</p>
			<p className="input-dw">・氏名のフリガナはカタカナになっていますか？</p>
			<p className="input-dw">・同意するの□にチェックはいれましかた？</p>
			<p className="input-dw">・「どこでメタロ・バランスを知りましたか」のアンケートは入力いたしましたか？</p>
			<div style={{ marginBottom: '2rem' }}></div>
			<p className="input-dw">※上記、すべて正しく入力しているにも関わらず、「次へ」のボタンがグレーのまま押せない場合は、お手数ですがお電話にてお申込を承りますので、<a href="tel:0120-785-602">0120-785-602</a>までお電話ください。その際、参考までにお客様のお使いのPCやスマートフォン、ブラウザ情報などを教えていただけますと幸いです。</p>
			<p className="input-dw">※端末、OS又はブラウザの組み合わせ、設定によっては正常に表示・動作しない可能性がありますのであらかじめご了承ください。</p>
	</>;
}
